import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import {
  listPpmInspectionPhotos,
  requestPpmInspectionPhotoUpload,
  confirmPpmInspectionPhoto,
  deletePpmInspectionPhoto,
  getListPpmInspectionPhotosQueryKey,
  useListPpmInspectionPhotos
} from "@workspace/api-client-react";
import type {
  PpmChecklistPhoto,
  PpmChecklistPhotoTaskKey,
  PpmChecklistPhotoStage,
  PpmChecklistPhotoContentType
} from "@workspace/api-client-react";

export function useUploadPpmPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      taskKey,
      stage,
      file,
    }: {
      id: number;
      taskKey: PpmChecklistPhotoTaskKey;
      stage: PpmChecklistPhotoStage;
      file: File;
    }) => {
      // 1. Request upload URL
      const contentType = file.type as PpmChecklistPhotoContentType;
      const res = await requestPpmInspectionPhotoUpload(id, {
        taskKey,
        stage,
        contentType,
        size: file.size,
      });

      // 2. Upload bytes
      const uploadRes = await fetch(res.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
      });
      if (!uploadRes.ok) {
        throw new Error("Failed to upload photo bytes");
      }

      // 3. Confirm upload
      const photo = await confirmPpmInspectionPhoto(id, res.photoId);
      return photo;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getListPpmInspectionPhotosQueryKey(variables.id),
      });
    },
  });
}

export function useDeletePpmPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, photoId }: { id: number; photoId: string }) => {
      await deletePpmInspectionPhoto(id, photoId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getListPpmInspectionPhotosQueryKey(variables.id),
      });
    },
  });
}
